// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type { Breakpoint } from '@mui/material/styles';
import { utils } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { formatStateOrStatus, getID } from '../../utils';
import { VisibleAtContainer } from '../common/containers/VisibleAtContainer';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import { Time } from '../common/time';
import { CropIdLink } from '../crop';
import HarvestLink from '../harvest/HarvestLink';
import { OrderIdLink } from '../order';
import { PlantingGroupIdLink } from '../plantingGroup';
import PlantingIdLink from './PlantingIdLink';

interface PlantingData {
  readonly id: string;
  readonly planting_time: number;
  readonly planting_status: string;
  readonly planting_state: string;
  readonly trays_rounded: string;
  readonly order_id?: string | null;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
  } | null;
  readonly tasks: {
    readonly count: number;
  };
}
interface Props {
  readonly plantings: PlantingData[];
  readonly useIndividualPlantingLinks?: boolean;
  readonly showOrder?: boolean;
}

export default function PlantingTable({ plantings, useIndividualPlantingLinks, showOrder = true }: Props) {
  const idValues: React.ReactElement[] = [];
  const statusValues: string[] = [];
  const timeValues: React.ReactElement[] = [];
  const traysValues: number[] = [];
  const cropValues: (React.ReactElement | string)[] = [];
  const orderValues: (React.ReactElement | string)[] = [];
  const taskCounts: number[] = [];
  for (const planting of plantings) {
    idValues.push(
      useIndividualPlantingLinks ? (
        <PlantingIdLink id={getID(planting.id)} />
      ) : (
        <PlantingGroupIdLink id={getID(planting.id)} />
      ),
    );
    statusValues.push(formatStateOrStatus(planting.planting_status));
    timeValues.push(
      <HarvestLink
        title={
          <VisibleAtContainer
            up={<Time time={planting.planting_time} />}
            down={<Time time={planting.planting_time} options={{ compactDisplay: true }} />}
          />
        }
        id={getID(planting.id)}
      />,
    );
    traysValues.push(Number.parseFloat(planting.trays_rounded));
    cropValues.push(planting.crop ? <CropIdLink id={getID(planting.crop.id)} name={planting.crop.crop_name} /> : '');
    orderValues.push(planting.order_id ? <OrderIdLink id={planting.order_id} /> : '');
    taskCounts.push(planting.tasks.count);
  }
  const columns: ColumnType[] = [
    {
      name: 'Planting',
      values: idValues,
    },
    {
      name: 'Status',
      values: statusValues,
    },
    {
      name: 'Trays',
      values: traysValues,
      numeric: true,
      visibleAt: 'sm' as Breakpoint,
    },
    showOrder
      ? {
          name: 'Order',
          values: orderValues,
          visibleAt: 'md' as Breakpoint,
        }
      : undefined,
    {
      name: 'Crop',
      values: cropValues,
      visibleAt: 'sm' as Breakpoint,
    },
    {
      name: 'Tasks',
      values: taskCounts,
      numeric: true,
      visibleAt: 'md' as Breakpoint,
    },
    {
      name: 'Harvest Date',
      values: timeValues,
    },
  ].filter(utils.notNull);

  return <Table columns={columns} />;
}
